import { Input, VStack, Checkbox, Collapsible } from "@chakra-ui/react"
import { Field } from "@hydrologiq/shared/chakra/Field"
import * as gqlTypes from "@hydrologiq/gql/graphql"

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import Form from "@hydrologiq/shared/chakra/custom/Form"
import Popover from "@hydrologiq/shared/chakra/Popover"
import ErrorAlert from "@hydrologiq/shared/components/ErrorAlert"
import { showSuccessToast, showErrorToast, showLoadingToast, dismissToast } from "../utils/toast"
import { useRoute } from "@hydrologiq/shared/providers/helpers/useRoute"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"
import { useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"

const CustomerCreate = () => {
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [createSite, setCreateSite] = useState(false)
  const [siteName, setSiteName] = useState("")
  const navigate = useNavigate()
  const [createCustomer, { loading }] = useMutationWithEndpoint(gqlTypes.CreateCustomerDocument, undefined, "primary")
  let route = useRoute()
  route = route.endsWith("/") ? route : `${route}/`

  const copy = useCopyValues({
    [COPY_KEYS.BUTTONS.CREATE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.BUTTONS.CREATE],
    [COPY_KEYS.HEADINGS.CUSTOMER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.CUSTOMER],
    [COPY_KEYS.HEADINGS.SITE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.SITE],
    [COPY_KEYS.MESSAGES.CREATED]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.CREATED],
    [COPY_KEYS.MESSAGES.SUCCESSFULLY_CREATED]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.SUCCESSFULLY_CREATED],
    [COPY_KEYS.MESSAGES.CREATION_FAILED]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.CREATION_FAILED],
    [COPY_KEYS.MESSAGES.VALIDATION_ERROR]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.VALIDATION_ERROR],
    [COPY_KEYS.MESSAGES.ADDING_TO_SYSTEM]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.ADDING_TO_SYSTEM],
    [COPY_KEYS.FORMS.PLEASE_PROVIDE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.FORMS.PLEASE_PROVIDE],
    [COPY_KEYS.FORMS.INFORMATION_BELOW]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.FORMS.INFORMATION_BELOW],
    [COPY_KEYS.FORMS.ENTER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.FORMS.ENTER],
    [COPY_KEYS.LABELS.NAME]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.LABELS.NAME],
    [COPY_KEYS.LABELS.SITE_NAME]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.LABELS.SITE_NAME],
    [COPY_KEYS.ACTIONS.CREATING]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.ACTIONS.CREATING],
    [COPY_KEYS.COMMON.A]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.COMMON.A],
  })

  return (
    <Popover
      actionType="create"
      placement="right-end"
      actionVariant="secondary"
      loading={loading}
      onOpenChange={setOpen}
      open={open}
    >
      <Form
        heading={`${copy[COPY_KEYS.BUTTONS.CREATE]} ${copy[COPY_KEYS.COMMON.A]} ${copy[
          COPY_KEYS.HEADINGS.CUSTOMER
        ].toLowerCase()}`}
        helperText={`${copy[COPY_KEYS.FORMS.PLEASE_PROVIDE]} ${copy[COPY_KEYS.HEADINGS.CUSTOMER].toLowerCase()} ${
          copy[COPY_KEYS.FORMS.INFORMATION_BELOW]
        }.`}
        actionType="create"
        loading={loading}
        onSubmit={async (data: FormData) => {
          let loadingToastId: string | undefined
          try {
            setError(null)

            // Show loading toast
            loadingToastId = showLoadingToast(
              `${copy[COPY_KEYS.ACTIONS.CREATING]} ${copy[COPY_KEYS.HEADINGS.CUSTOMER]}`,
              copy[COPY_KEYS.MESSAGES.ADDING_TO_SYSTEM]
            )

            const name = data.get("name")?.toString()
            if (!name || !name.trim()) {
              const errorMessage = `${copy[COPY_KEYS.FORMS.PLEASE_PROVIDE]} ${copy[COPY_KEYS.COMMON.A]} ${copy[
                COPY_KEYS.HEADINGS.CUSTOMER
              ].toLowerCase()} ${copy[COPY_KEYS.LABELS.NAME].toLowerCase()}`
              setError(errorMessage)
              showErrorToast(copy[COPY_KEYS.MESSAGES.VALIDATION_ERROR], errorMessage)
              return false
            }

            // Validate site data if creating a site
            if (createSite) {
              if (!siteName || !siteName.trim()) {
                const errorMessage = `${copy[COPY_KEYS.FORMS.PLEASE_PROVIDE]} ${copy[COPY_KEYS.COMMON.A]} ${copy[
                  COPY_KEYS.HEADINGS.SITE
                ].toLowerCase()} ${copy[COPY_KEYS.LABELS.NAME].toLowerCase()}`
                setError(errorMessage)
                showErrorToast(copy[COPY_KEYS.MESSAGES.VALIDATION_ERROR], errorMessage)
                return false
              }
            }

            // Prepare mutation variables
            const customerInput: gqlTypes.CustomerInput = {
              name: name.trim(),
            }

            if (createSite) {
              customerInput.site = {
                name: siteName.trim(),
              }
            }

            const result = await createCustomer({ variables: { customer: customerInput } })

            if (result.data?.createCustomer) {
              const createdCustomer = result.data.createCustomer
              const hasSite = createdCustomer.sites && createdCustomer.sites.length > 0

              showSuccessToast(
                `${copy[COPY_KEYS.HEADINGS.CUSTOMER]} ${copy[COPY_KEYS.MESSAGES.CREATED]}`,
                `${copy[COPY_KEYS.HEADINGS.CUSTOMER]} "${name}"${
                  hasSite ? ` and ${copy[COPY_KEYS.HEADINGS.SITE].toLowerCase()}` : ""
                } ${copy[COPY_KEYS.MESSAGES.SUCCESSFULLY_CREATED]}.`
              )

              setOpen(false)
              setError(null)

              // Navigate to appropriate route based on whether a site was created
              if (hasSite) {
                navigate(`${route}${createdCustomer.id}/sites`)
              } else {
                navigate(`${route}${createdCustomer.id}`)
              }

              return true
            } else {
              const errorMessage = `Failed to create ${copy[
                COPY_KEYS.HEADINGS.CUSTOMER
              ].toLowerCase()}. Please try again.`
              setError(errorMessage)
              showErrorToast(copy[COPY_KEYS.MESSAGES.CREATION_FAILED], errorMessage)
              return false
            }
          } catch (error) {
            const errorMessage =
              error instanceof Error
                ? error.message
                : `An unexpected error occurred while creating the ${copy[COPY_KEYS.HEADINGS.CUSTOMER].toLowerCase()}`
            setError(errorMessage)
            showErrorToast(copy[COPY_KEYS.MESSAGES.CREATION_FAILED], errorMessage)
            return false
          } finally {
            // Always dismiss loading toast
            if (loadingToastId) {
              dismissToast(loadingToastId)
            }
          }
        }}
        onCancel={() => {
          setError(null)
          setOpen(false)
          setCreateSite(false)
          setSiteName("")
        }}
      >
        <VStack gap={5}>
          {error && (
            <ErrorAlert
              title={`${copy[COPY_KEYS.HEADINGS.CUSTOMER]} Creation Error`}
              message={error}
              onClose={() => setError(null)}
            />
          )}
          <Field label={copy[COPY_KEYS.LABELS.NAME]} required>
            <Input
              name="name"
              placeholder={`${copy[COPY_KEYS.FORMS.ENTER]} ${copy[COPY_KEYS.HEADINGS.CUSTOMER].toLowerCase()} ${copy[
                COPY_KEYS.LABELS.NAME
              ].toLowerCase()}`}
            />
          </Field>

          <Field>
            <Checkbox.Root checked={createSite} onCheckedChange={(e) => setCreateSite(!!e.checked)}>
              <Checkbox.HiddenInput />
              <Checkbox.Control />
              <Checkbox.Label>
                Also create a {copy[COPY_KEYS.HEADINGS.SITE].toLowerCase()} for this{" "}
                {copy[COPY_KEYS.HEADINGS.CUSTOMER].toLowerCase()}
              </Checkbox.Label>
            </Checkbox.Root>
          </Field>

          <Collapsible.Root open={createSite} width="100%">
            <Collapsible.Content width="100%">
              <VStack gap={4} width="100%">
                <Field label={copy[COPY_KEYS.LABELS.SITE_NAME]} required={createSite}>
                  <Input
                    value={siteName}
                    onChange={(e) => setSiteName(e.target.value)}
                    placeholder={`Enter ${copy[COPY_KEYS.HEADINGS.SITE].toLowerCase()} name`}
                  />
                </Field>
              </VStack>
            </Collapsible.Content>
          </Collapsible.Root>
        </VStack>
      </Form>
    </Popover>
  )
}

export default CustomerCreate
